import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { authApi, fetchAndCacheOrgId, type Customer } from '@/lib/api/auth';
import { clearGuestAddress } from '@/lib/guestAddress';

const TOKEN_KEY = 'luvelle_access_token';

type AuthState =
  | { status: 'loading' }
  | { status: 'guest' }
  | { status: 'authenticated'; token: string; customer: Customer };

interface AuthContextValue {
  auth: AuthState;
  login: (token: string, customer: Customer) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    // Fetch org ID first (needed by all auth API calls), then hydrate session.
    fetchAndCacheOrgId().finally(() => {
      if (!token) {
        setAuth({ status: 'guest' });
        return;
      }
      authApi
        .getMe(token)
        .then((customer) => setAuth({ status: 'authenticated', token, customer }))
        .catch(() => {
          localStorage.removeItem(TOKEN_KEY);
          setAuth({ status: 'guest' });
        });
    });
  }, []);

  const login = useCallback((token: string, customer: Customer) => {
    localStorage.setItem(TOKEN_KEY, token);
    setAuth({ status: 'authenticated', token, customer });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    // Drop any leftover guest pin so the next visitor on this browser
    // doesn't inherit the previous customer's last-used location.
    clearGuestAddress();
    setAuth({ status: 'guest' });
  }, []);

  const value = useMemo(() => ({ auth, login, logout }), [auth, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
